<style lang="scss" src="./VChart.scss"/>

<script lang="ts">

import Decimal from 'decimal.js';
import Core from '@/core/Core';
import DateSimple from '@/core/types/DateSimple';
import ChartLayer from './ChartLayer';
import type { ChartColor } from './ChartLayer';
import ChartScale from './ChartScale';
import ChartLayout from './ChartLayout';
import type ChartAction from './ChartAction';
import ChartDot from './ChartDot';
import ChartCluster from './ChartCluster';

/** Поля вокруг области построения. Сверху — под подпись единицы шкалы; слева — по ширине подписей делений, но не меньше side */
const MARGIN = { top: 18, bottom: 32, side: 12 };

/** Шаги зума колесиком: окно оси времени — период, делённый на шаг */
const ZOOM_STEPS = [1, 2, 3];

/** Сдвиг окна за один щелчок колесика при прокрутке по горизонтали — доля ширины окна */
const PAN_STEP_SHARE = 0.15;

/** Отступ подписи деления от рамки */
const TICK_GAP_PX = 6;

/** Размер шрифта подписей делений — им же меряется ширина колонки подписей */
const TICK_FONT_SIZE_PX = 11;

/** Контекст canvas для измерения ширины текста без DOM; один на все графики */
let measureContext: CanvasRenderingContext2D | null = null;

/** Ключ localStorage для темы графика — одна на все графики */
const THEME_STORAGE_KEY = 'v-chart:theme';

const DOT_RADIUS = 4;

const CLUSTER_RADIUS = 8;

/** Полуширина засечки на начале отрезка VChartRanges */
const RANGE_TICK_HALF_WIDTH = 5;

/** Размер экранной ячейки кластеризации: точки ближе диаметра друг к другу не различить */
const CLUSTER_CELL_PX = DOT_RADIUS * 2;

/** Доля высоты графика под панель объёма — она внизу, под панелью цены */
const VOLUME_PANEL_SHARE = 0.22;

/** Зазор между панелями цены и объёма — в нём подпись единицы объёма */
const PANEL_GAP_PX = 20;

/** Вертикали сетки по месяцам реже не ставим — иначе сетка сливается */
const X_TICK_MIN_SPACING_PX = 36;

/** Подпись года ставится, только если участок года в окне не уже этого */
const YEAR_LABEL_MIN_WIDTH_PX = 40;

/** Шаги делений по оси времени в месяцах, от частого к редкому */
const X_TICK_MONTH_STEPS = [1, 2, 3, 6, 12, 24];

const Y_TICK_SPACING_PX = 48;

/** Запас сверху и снизу от крайних цен, доля диапазона */
const Y_PADDING_SHARE = 0.08;

/** Строк в тултипе кластера не больше — дальше «и ещё N» */
const TOOLTIP_MAX_ROWS = 12;

/** Группа строк тултипа одного слоя */
type TooltipGroup = { label: string; color: ChartColor | 'mixed'; isHollow: boolean; rows: { title: string; action: ChartAction | null }[] };

/** Прямоугольник наведённого элемента внутри области графика — к нему привязывается тултип */
type TooltipAnchor = { left: number; top: number; width: number; height: number };

/**
 * VChart — график по оси времени: точки, ступенчатые линии и столбики объёма.
 *
 * Состав задаётся вложенными тегами-слоями, как колонки в VDataGridV2:
 * VChartPoints, VChartStepLine, VChartBars, VChartRanges. Компонент читает vnode слота сам.
 *
 * Правила:
 * - ось X — период [from, to]; всё, что вне периода, не рисуется;
 * - две панели с общей осью времени: сверху цена (точки, ступени, отрезки), снизу объём (столбики);
 *   панель объёма — только если есть объёмы, подписи обеих шкал слева;
 * - в панели цены поверх ступеней отрезки, поверх всего точки;
 * - точки всех слоёв, попавшие в одну экранную ячейку, собираются в кластер с числом,
 *   тултип кластера показывает все его точки по слоям;
 * - легенда над графиком или под ним (проп legend), выровнена по рамке; клик по ней прячет и показывает слой,
 *   спрятанный слой не участвует в шкалах; справа в той же строке — слот #toolbar и переключатель темы;
 * - единицы шкал (valueUnit, volumeUnit) подписаны над панелями;
 * - тёмная тема переключается кнопкой у легенды и запоминается для всех графиков;
 * - строки тултипа вида «Подпись: значение» выделяют значение;
 * - колесико — зум оси времени шагами ×1…×3 относительно даты под курсором; сдвиг окна — перетаскиванием мышью,
 *   Shift+колесико или горизонтальной прокруткой;
 *   шкала цены при зуме не меняется, под осью — индикатор видимого окна;
 * - значения — уже готовые числа: валюты, НДС и прочую предметную область график не знает.
 *
 * Тексты тултипов приходят с данными (ChartPoint.title и т.п.), строки через \n.
 *
 * Слот #toolbar — элементы управления справа в строке легенды (например, период). Слот #description — подпись под графиком.
 *
 * @example
 * <VChart :from="period.from" :to="period.to" value-unit="UZS за кг" volume-unit="кг">
 *   <VChartBars label="Объём заказов" color="green" :bars="volumes"/>
 *   <VChartStepLine label="Бюджетная цена" color="red" :steps="budget"/>
 *   <VChartPoints label="Заказы" color="yellow" :points="orders"/>
 *
 *   <template #description>
 *     Сделок без курса на дату: 2
 *   </template>
 * </VChart>
 */
export default defineComponent({
  name: 'VChart',

  props: {
    /** Левая граница периода */
    from: {
      type:     Date as PropType<Date>,
      required: true,
    },

    /** Правая граница периода */
    to: {
      type:     Date as PropType<Date>,
      required: true,
    },

    /** Высота в пикселях; ширина — по контейнеру */
    height: {
      type:    Number,
      default: 360,
    },

    /** Единица шкалы цены, подписывается над панелью цены */
    valueUnit: {
      type:    String,
      default: '',
    },

    /** Единица шкалы объёма, подписывается над панелью объёма */
    volumeUnit: {
      type:    String,
      default: '',
    },

    /** Текст поперёк пустого графика */
    emptyText: {
      type:    String,
      default: 'Нет данных за выбранный период',
    },

    /** Легенда над графиком или под ним */
    legend: {
      type:      String as PropType<'top' | 'bottom'>,
      default:   'top',
      validator: (v: string) => ['top', 'bottom'].includes(v),
    },

    /** Выбранный период (например, кликом по колонке объёма) — постоянная подсветка сквозь обе панели */
    selectedPeriod: {
      type:    Object as PropType<{ from: Date; to: Date } | null>,
      default: null,
    },

    /** Данные ещё не готовы (например, едут курсы) — вместо графика надпись загрузки */
    loading: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['update:dark'],

  data() {
    return {
      /** Ширина области графика, от ResizeObserver */
      width: 0,

      /** Подписи слоёв, спрятанных кликом по легенде */
      hiddenLabels: [] as string[],

      hover: null as null | { anchor: TooltipAnchor; groups: TooltipGroup[]; band: { x: number; width: number } | null },

      /** Растёт на каждое наведение — тултип пересоздаётся и встаёт к новому элементу */
      hoverKey: 0,

      /** Отложенное скрытие тултипа: уход с элемента на сам тултип не должен его закрывать */
      hideTimer: null as number | null,

      resizeObserver: null as ResizeObserver | null,

      /** Тёмная тема — выбор пользователя, общий для всех графиков */
      isDark: false,

      /** Шаг зума из ZOOM_STEPS; 1 — весь период */
      zoom: 1,

      /** Начало видимого окна оси времени при зуме, мс */
      viewStart: 0,

      /** Период, к которому относится зум: сменился период — окно сбрасывается на весь период */
      zoomPeriodKey: '',

      /** Перетаскивание окна мышью: X нажатия (null — не тащим) и начало окна в момент нажатия */
      dragStartX: null as number | null,
      dragStartView: 0,

      /** Мышь сдвинулась при нажатии — это перетаскивание, а не клик: клик по колонке после него не срабатывает */
      isDragMoved: false,
    };
  },

  created() {
    try {
      this.isDark = localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
    } catch (error) {
      this.isDark = false;
    }

    // Тему знает и потребитель — например, чтобы перекрасить карточку вокруг графика
    this.$emit('update:dark', this.isDark);
  },

  mounted() {
    this.resizeObserver = markRaw(new ResizeObserver((entries) => {
      this.width = entries[0].contentRect.width;
    }));

    this.resizeObserver.observe(this.$refs.plot as HTMLElement);
  },

  beforeUnmount() {
    this.resizeObserver?.disconnect();
    this.cancelHide();
    this.onDragEnd();
  },

  methods: {
    toggleLayer(label: string): void {
      if (this.hiddenLabels.includes(label) === true) {
        this.hiddenLabels = this.hiddenLabels.filter(hidden => hidden !== label);
      } else {
        this.hiddenLabels = [...this.hiddenLabels, label];
      }
    },

    /** Подпись на оси: сокращённо (12,5 тыс, 3,45 млн) — на оси мало места */
    formatTick(value: number): string {
      return Core.utils.number.formatDecimalShorten(new Decimal(value), 2);
    },

    /** Тултип из точек: по группе на слой, не больше TOOLTIP_MAX_ROWS строк на всех */
    showDotsTooltip(event: MouseEvent, dots: ChartDot[]): void {
      const groups = new Map<ChartLayer, TooltipGroup>();
      let rowsCount = 0;

      for (const dot of dots) {
        if (rowsCount < TOOLTIP_MAX_ROWS) {
          let group = groups.get(dot.layer);

          if (group === undefined) {
            group = { label: dot.layer.label, color: dot.layer.color, isHollow: dot.layer.isHollow, rows: [] };
            groups.set(dot.layer, group);
          }

          group.rows.push({ title: dot.point.title, action: dot.point.action });
          rowsCount++;
        }
      }

      const result = [...groups.values()];

      if (dots.length > TOOLTIP_MAX_ROWS) {
        result.push({ label: `и ещё ${dots.length - TOOLTIP_MAX_ROWS}`, color: 'mixed', isHollow: false, rows: [] });
      }

      this.openTooltip(this.anchorOf(event, false), result, null);
    },

    /**
     * Тултип одной записи. atPointer — якорь в точке курсора, а не весь элемент:
     * у длинной ступени тултип иначе встал бы под серединой линии
     */
    showTooltip(
      event: MouseEvent,
      layer: ChartLayer,
      title: string,
      action: ChartAction | null,
      atPointer: boolean,
      band: { x: number; width: number } | null = null,
    ): void {
      this.openTooltip(this.anchorOf(event, atPointer), [{ label: layer.label, color: layer.color, isHollow: layer.isHollow, rows: [{ title, action }] }], band);
    },

    /** Наведение сразу заменяет тултип — без задержки, чтобы вести мышью по графику и смотреть */
    /** band — полоса периода, подсвечиваемая сквозь обе панели (при наведении на месяц объёма) */
    openTooltip(anchor: TooltipAnchor, groups: TooltipGroup[], band: { x: number; width: number } | null = null): void {
      this.cancelHide();
      this.hoverKey++;
      this.hover = { anchor, groups, band };
    },

    /**
     * Скрытие откладывается до конца текущего события: уход с элемента на тултип (через его cloud-маску)
     * приходит той же цепочкой mouseleave → mouseenter, и mouseenter тултипа успевает отменить скрытие.
     * Видимой задержки нет
     */
    scheduleHide(): void {
      this.cancelHide();
      this.hideTimer = window.setTimeout(() => {
        this.hideTimer = null;
        this.hover = null;
      }, 0);
    },

    cancelHide(): void {
      if (this.hideTimer !== null) {
        window.clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },

    /** Прямоугольник наведённого элемента (или точка курсора) в координатах области графика */
    anchorOf(event: MouseEvent, atPointer: boolean): TooltipAnchor {
      const plotRect = (this.$refs.plot as HTMLElement).getBoundingClientRect();

      if (atPointer === true) {
        return { left: event.clientX - plotRect.left, top: event.clientY - plotRect.top, width: 1, height: 1 };
      } else {
        const rect = (event.currentTarget as Element).getBoundingClientRect();

        return { left: rect.left - plotRect.left, top: rect.top - plotRect.top, width: rect.width, height: rect.height };
      }
    },

    /** Есть ли у видимых слоёв хоть что-то внутри периода — иначе график пустой */
    hasDataInPeriod(layers: ChartLayer[]): boolean {
      const fromTime = this.from.getTime();
      const toTime = this.to.getTime();

      return layers.some(layer =>
        layer.points.some(point => point.date.getTime() >= fromTime && point.date.getTime() <= toTime)
        || layer.steps.some(step => step.start.getTime() <= toTime && (step.end === null || step.end.getTime() > fromTime))
        || layer.bars.some(bar => bar.start.getTime() <= toTime && bar.end.getTime() > fromTime)
        || layer.ranges.some(range => range.date.getTime() >= fromTime && range.date.getTime() <= toTime)
      );
    },

    /** Деления оси времени — первые числа месяцев с шагом, при котором подписи не налезают */
    timeTicks(xScale: ChartScale): Date[] {
      const domainFrom = new DateSimple(new Date(xScale.domainStart));
      const domainTo = new DateSimple(new Date(xScale.domainEnd));
      const months = (domainTo.getFullYear() - domainFrom.getFullYear()) * 12 + domainTo.getMonth() - domainFrom.getMonth() + 1;
      const pxPerMonth = (xScale.rangeEnd - xScale.rangeStart) / months;

      let step = X_TICK_MONTH_STEPS[X_TICK_MONTH_STEPS.length - 1];

      for (const candidate of [...X_TICK_MONTH_STEPS].reverse()) {
        if (pxPerMonth * candidate >= X_TICK_MIN_SPACING_PX) {
          step = candidate;
        }
      }

      const result: Date[] = [];
      let tick = domainFrom.startOfMonth();

      while (tick.getTime() <= xScale.domainEnd) {
        if (tick.getTime() >= xScale.domainStart && (tick.getFullYear() * 12 + tick.getMonth()) % step === 0) {
          result.push(tick);
        }

        tick = tick.addMonths(1);
      }

      return result;
    },

    /** Строка легенды и тулбара. Выравнивается по рамке области построения, а не по краю блока */
    renderLegend(layers: ChartLayer[], layout: ChartLayout | null): VNode {
      const items = layers.map(layer => h('li', {
        class:   { hidden: this.hiddenLabels.includes(layer.label) },
        onClick: () => this.toggleLayer(layer.label),
      }, [
        h('span', { class: ['swatch', layer.kind, layer.color, { hollow: layer.isHollow, dashed: layer.isDashed }] }),
        layer.label,
      ]));

      const style: Record<string, string> = {};

      if (layout !== null) {
        style.paddingLeft = `${layout.plotLeft}px`;
        style.paddingRight = `${this.width - layout.plotRight}px`;
      }

      return h('section', { class: 'legend-bar', style }, [
        h('ul', { class: 'legend' }, items),
        h('div', { class: 'toolbar' }, this.$slots.toolbar?.()),
        h(resolveComponent('VLink'), {
          material: this.isDark === true ? 'light_mode' : 'dark_mode',
          title:    this.isDark === true ? 'Светлая тема графика' : 'Тёмная тема графика',
          onClick:  this.toggleTheme,
        }),
      ]);
    },

    /**
     * Тултип — VPopup у невидимого якоря поверх наведённого элемента. VPopup меряет якорь через offsetWidth,
     * которого у элементов svg нет, поэтому якорь — html-блок с прямоугольником элемента.
     * Cloud-маска VPopup держит тултип открытым, пока курсор идёт к нему от элемента — по ссылке можно кликнуть
     */
    renderTooltip(): VNode[] {
      if (this.hover === null) {
        return [];
      } else {
        const anchorId = `v-chart-anchor-${this.$.uid}`;
        const anchor = this.hover.anchor;

        // Тултип без ссылок не нужно удерживать мышью — он пассивный и не перехватывает наведение и клики
        const isPassive = this.hover.groups.every(group => group.rows.every(row => row.action === null));

        const sections = this.hover.groups.map(group => h('section', [
          h('header', [
            h('span', { class: ['swatch', 'points', group.color, { hollow: group.isHollow }] }),
            group.label,
          ]),
          ...group.rows.map(row => h('p', row.title.split('\n').map((line, index) => {
            if (index === 0 && row.action !== null && line.includes(row.action.label) === true) {
              return this.renderTooltipActionLine(line, row.action);
            } else {
              return this.renderTooltipLine(line);
            }
          }))),
        ]));

        return [
          h('div', {
            id:    anchorId,
            class: 'tooltip-anchor',
            style: { left: `${anchor.left}px`, top: `${anchor.top}px`, width: `${anchor.width}px`, height: `${anchor.height}px` },
          }),
          h(resolveComponent('VPopup'), {
            key:                 this.hoverKey,
            target:              `#${anchorId}`,
            visible:             true,
            position:            'bottom',
            offset:              8,
            noPointer:           true,
            cssClasses:          ['v-chart-tooltip', ...(this.isDark === true ? ['dark'] : []), ...(isPassive === true ? ['passive'] : [])],
            onMouseenter:        this.cancelHide,
            onMouseleave:        this.scheduleHide,
            'onUpdate:visible':  (visible: boolean) => {
              if (visible === false) {
                this.hover = null;
              }
            },
          }, { default: () => sections }),
        ];
      }
    },

    /**
     * Строка тултипа. «Подпись: значение» делится по первому двоеточию — значение выделяется,
     * подпись приглушается; строка без двоеточия выводится как есть. Длинное обрезается многоточием
     */
    renderTooltipLine(line: string): VNode {
      const index = line.indexOf(': ');

      if (index === -1) {
        return h('span', { class: ['ellipsis', 'value'] }, line);
      } else {
        return h('span', { class: 'ellipsis' }, [
          h('span', { class: 'label' }, line.slice(0, index + 1)),
          ' ',
          h('span', { class: 'value' }, line.slice(index + 2)),
        ]);
      }
    },

    /** Первая строка записи, в которой подпись действия (например, номер документа) становится ссылкой */
    renderTooltipActionLine(line: string, action: ChartAction): VNode {
      const index = line.indexOf(action.label);

      return h('span', { class: ['ellipsis', 'value'] }, [
        line.slice(0, index),
        h(resolveComponent('VLink'), { onClick: action.callback }, { default: () => action.label }),
        line.slice(index + action.label.length),
      ]);
    },

    /** Видимое окно оси времени: при зуме — часть периода, иначе весь период. Смена периода сбрасывает зум */
    viewWindow(): { start: number; end: number } {
      const fullStart = this.from.getTime();
      const fullEnd = this.to.getTime();

      if (this.zoom === 1 || this.zoomPeriodKey !== `${fullStart}-${fullEnd}`) {
        return { start: fullStart, end: fullEnd };
      } else {
        const span = (fullEnd - fullStart) / this.zoom;
        const start = Math.min(Math.max(this.viewStart, fullStart), fullEnd - span);

        return { start, end: start + span };
      }
    },

    /**
     * Колесико над графиком: вертикальное — зум шагами ZOOM_STEPS относительно даты под курсором,
     * горизонтальное или с Shift — сдвиг окна. Над графиком колесико принадлежит только ему:
     * на краях диапазона зума оно не проваливается в прокрутку диалога и в зум страницы
     */
    onWheel(event: WheelEvent): void {
      // overscroll-behavior тут не поможет — график не прокручиваемый контейнер, поэтому гасим событие целиком
      event.preventDefault();

      const frame = (this.$refs.plot as HTMLElement).querySelector('rect.frame');

      if (frame !== null) {
        const fullStart = this.from.getTime();
        const fullEnd = this.to.getTime();
        const periodKey = `${fullStart}-${fullEnd}`;

        if (this.zoomPeriodKey !== periodKey) {
          this.zoomPeriodKey = periodKey;
          this.zoom = 1;
        }

        const view = this.viewWindow();
        const isPan = event.shiftKey === true || Math.abs(event.deltaX) > Math.abs(event.deltaY);

        if (isPan === true) {
          if (this.zoom > 1) {
            const delta = event.deltaX !== 0 ? event.deltaX : event.deltaY;
            const span = view.end - view.start;

            this.viewStart = Math.min(Math.max(view.start + Math.sign(delta) * span * PAN_STEP_SHARE, fullStart), fullEnd - span);
          }
        } else {
          const index = ZOOM_STEPS.indexOf(this.zoom);
          const nextIndex = Math.min(Math.max(index + (event.deltaY < 0 ? 1 : -1), 0), ZOOM_STEPS.length - 1);

          if (nextIndex !== index) {
            const rect = frame.getBoundingClientRect();
            const share = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
            const anchorTime = view.start + share * (view.end - view.start);
            const nextSpan = (fullEnd - fullStart) / ZOOM_STEPS[nextIndex];

            this.zoom = ZOOM_STEPS[nextIndex];
            this.viewStart = Math.min(Math.max(anchorTime - share * nextSpan, fullStart), fullEnd - nextSpan);
          }
        }
      }
    },

    /** Нажатие на графике при зуме — начало перетаскивания окна */
    onMouseDown(event: MouseEvent): void {
      this.isDragMoved = false;

      if (this.zoom > 1 && event.button === 0) {
        this.dragStartX = event.clientX;
        this.dragStartView = this.viewWindow().start;

        window.addEventListener('mousemove', this.onDragMove);
        window.addEventListener('mouseup', this.onDragEnd);

        // Без этого при перетаскивании выделяется текст подписей
        event.preventDefault();
      }
    },

    /** Окно едет за мышью; сдвиг меньше 3 px — ещё клик, а не перетаскивание */
    onDragMove(event: MouseEvent): void {
      const frame = (this.$refs.plot as HTMLElement | undefined)?.querySelector('rect.frame');

      if (this.dragStartX !== null && Core.utils.isNullOrUndefined(frame) === false) {
        const dx = event.clientX - this.dragStartX;

        if (Math.abs(dx) > 3) {
          this.isDragMoved = true;
        }

        if (this.isDragMoved === true) {
          const fullStart = this.from.getTime();
          const fullEnd = this.to.getTime();
          const span = (fullEnd - fullStart) / this.zoom;

          this.viewStart = Math.min(Math.max(this.dragStartView - dx / frame!.getBoundingClientRect().width * span, fullStart), fullEnd - span);
        }
      }
    },

    onDragEnd(): void {
      this.dragStartX = null;

      window.removeEventListener('mousemove', this.onDragMove);
      window.removeEventListener('mouseup', this.onDragEnd);
    },

    toggleTheme(): void {
      this.isDark = this.isDark === false;
      this.$emit('update:dark', this.isDark);

      try {
        localStorage.setItem(THEME_STORAGE_KEY, this.isDark === true ? 'dark' : 'light');
      } catch (error) {
        // Хранилище недоступно (приватный режим) — тема просто не запомнится
      }
    },

    /** Разметка: панели, шкалы и поля по данным видимых слоёв в периоде */
    computeLayout(layers: ChartLayer[]): ChartLayout {
      const plotTop = MARGIN.top;
      const plotBottom = this.height - MARGIN.bottom;

      const fromTime = this.from.getTime();
      const toTime = this.to.getTime();

      const values: number[] = [];
      const volumes: number[] = [];

      for (const layer of layers) {
        for (const point of layer.points) {
          if (point.date.getTime() >= fromTime && point.date.getTime() <= toTime) {
            values.push(point.value);
          }
        }

        for (const step of layer.steps) {
          if (step.start.getTime() <= toTime && (step.end === null || step.end.getTime() > fromTime)) {
            values.push(step.value);
          }
        }

        for (const bar of layer.bars) {
          if (bar.start.getTime() <= toTime && bar.end.getTime() > fromTime) {
            volumes.push(bar.value);
          }
        }

        for (const range of layer.ranges) {
          if (range.date.getTime() >= fromTime && range.date.getTime() <= toTime) {
            values.push(range.start);

            if (range.end !== null) {
              values.push(range.end);
            }
          }
        }
      }

      // Цена и объём — две отдельные панели с общей осью времени: на общей панели столбики
      // перекрывают цены, а у дешёвых позиций (центы) шкалы несопоставимы совсем.
      // Панель объёма появляется, только если есть объёмы
      const maxVolume = Math.max(0, ...volumes);

      let priceBottom: number;
      let volumeTop: number;

      if (maxVolume > 0) {
        volumeTop = plotBottom - (plotBottom - plotTop) * VOLUME_PANEL_SHARE;
        priceBottom = volumeTop - PANEL_GAP_PX;
      } else {
        volumeTop = plotBottom;
        priceBottom = plotBottom;
      }

      // Шкалы по вертикали считаются до горизонтальных полей: ширину левого поля задают подписи делений
      let yScale: ChartScale | null = null;
      let yTicks: number[] = [];

      if (values.length > 0) {
        let min = Math.min(...values);
        let max = Math.max(...values);

        if (min === max) {
          if (min === 0) {
            max = 1;
          } else {
            min = min * 0.9;
            max = max * 1.1;
          }
        }

        const padding = (max - min) * Y_PADDING_SHARE;
        const tickCount = Math.max(2, Math.floor((priceBottom - plotTop) / Y_TICK_SPACING_PX));

        yScale = new ChartScale(Math.max(min - padding, 0), max + padding, priceBottom, plotTop);
        yTicks = ChartScale.ticks(yScale.domainStart, yScale.domainEnd, tickCount);
      }

      let volumeScale: ChartScale | null = null;
      let volumeTicks: number[] = [];

      if (maxVolume > 0) {
        // Верх шкалы — сам максимум, без округления вверх: «круглый» верх при максимуме чуть больше круглого числа
        // (100 600 → 200 000) оставлял самый высокий столбик на половине панели. Круглыми остаются только деления.
        // Сверху запас под половину высоты подписи верхнего деления
        volumeScale = new ChartScale(0, maxVolume, plotBottom, volumeTop + 6);
        volumeTicks = ChartScale.ticks(0, maxVolume, 3).filter(tick => tick > 0);
      }

      // Подписи обеих шкал — слева, одной колонкой; справа только минимальное поле
      const plotLeft = Math.max(MARGIN.side, this.ticksWidth([...yTicks, ...volumeTicks]) + TICK_GAP_PX + 1);
      const plotRight = this.width - MARGIN.side;
      // По X — видимое окно (при зуме), шкала цены остаётся общей на весь период
      const view = this.viewWindow();
      const xScale = new ChartScale(view.start, view.end, plotLeft, plotRight);

      return new ChartLayout(plotLeft, plotRight, plotTop, plotBottom, priceBottom, volumeTop, xScale, yScale, yTicks, volumeScale, volumeTicks);
    },

    /** Сетка, подписи осей и все слои — содержимое svg */
    renderPlot(layers: ChartLayer[], layout: ChartLayout): VNode[] {
      const { plotLeft, plotRight, plotTop, plotBottom, priceBottom, volumeTop, xScale, yScale, yTicks, volumeScale, volumeTicks } = layout;

      const grid: VNode[] = [];
      const fillNodes: VNode[] = [];
      const barNodes: VNode[] = [];
      const stepNodes: VNode[] = [];
      const rangeNodes: VNode[] = [];
      const dotNodes: VNode[] = [];

      // Подложки панелей: заливка в рамке, сетка поверх
      if (yScale !== null) {
        grid.push(h('rect', { class: 'frame', x: plotLeft, y: plotTop, width: plotRight - plotLeft, height: priceBottom - plotTop }));
      }

      if (volumeScale !== null) {
        grid.push(h('rect', { class: 'frame', x: plotLeft, y: volumeTop, width: plotRight - plotLeft, height: plotBottom - volumeTop }));
      }

      const bands: { x: number; width: number; cssClass: string }[] = [];

      if (this.selectedPeriod !== null) {
        const x = xScale.toPixel(Math.max(this.selectedPeriod.from.getTime(), xScale.domainStart));

        bands.push({ x, width: xScale.toPixel(Math.min(this.selectedPeriod.to.getTime(), xScale.domainEnd)) - x, cssClass: 'band selected' });
      }

      if (this.hover !== null && this.hover.band !== null) {
        bands.push({ x: this.hover.band.x, width: this.hover.band.width, cssClass: 'band' });
      }

      // Полосы выбранного и наведённого периода — одним прямоугольником сквозь обе панели и зазор между ними,
      // под всеми слоями. Без панели цены полоса начинается от панели объёма
      const bandTop = yScale !== null ? plotTop : volumeTop;
      const bandBottom = volumeScale !== null ? plotBottom : priceBottom;

      for (const band of bands) {
        // Выбранный месяц может оказаться за пределами окна при зуме
        if (band.width > 0) {
          grid.push(h('rect', { class: band.cssClass, x: band.x, y: bandTop, width: band.width, height: bandBottom - bandTop }));
        }
      }

      // Вертикаль сетки в обеих панелях; year — граница года, пунктиром
      const pushVertical = (x: number, cssClass: string) => {
        if (yScale !== null) {
          grid.push(h('line', { class: cssClass, x1: x, x2: x, y1: plotTop, y2: priceBottom }));
        }

        if (volumeScale !== null) {
          grid.push(h('line', { class: cssClass, x1: x, x2: x, y1: volumeTop, y2: plotBottom }));
        }
      };

      // Ось времени — вертикали сетки по месяцам без подписей; на 1 января вместо обычной вертикали — граница года
      for (const tick of this.timeTicks(xScale)) {
        if (tick.getMonth() !== 0) {
          pushVertical(xScale.toPixel(tick.getTime()), 'grid');
        }
      }

      // Подписи — только годы, по центру участка года между границами (крайние участки обрезаны окном).
      // Полные даты на оси были визуальным мусором: точная дата есть в тултипах и таблице
      const firstYear = new Date(xScale.domainStart).getFullYear();
      const lastYear = new Date(xScale.domainEnd).getFullYear();

      for (let year = firstYear; year <= lastYear; year++) {
        const yearStart = new DateSimple(new Date(year, 0, 1)).getTime();
        const sectionStart = xScale.toPixel(Math.max(yearStart, xScale.domainStart));
        const sectionEnd = xScale.toPixel(Math.min(new DateSimple(new Date(year + 1, 0, 1)).getTime(), xScale.domainEnd));

        if (year > firstYear) {
          pushVertical(xScale.toPixel(yearStart), 'grid year');
        }

        // Узкий обрезок года на краю окна не подписываем — подпись в нём не помещается
        if (sectionEnd - sectionStart >= YEAR_LABEL_MIN_WIDTH_PX) {
          grid.push(h('text', { class: 'tick x', x: (sectionStart + sectionEnd) / 2, y: plotBottom + 16 }, String(year)));
        }
      }

      // Индикатор окна при зуме: тонкая дорожка под осью времени, подсвеченная часть — видимое окно
      if (this.zoom > 1) {
        const fullStart = this.from.getTime();
        const fullSpan = this.to.getTime() - fullStart;
        const trackY = this.height - 4;
        const thumbX = plotLeft + (xScale.domainStart - fullStart) / fullSpan * (plotRight - plotLeft);
        const thumbWidth = (xScale.domainEnd - xScale.domainStart) / fullSpan * (plotRight - plotLeft);

        grid.push(h('line', { class: 'zoom-track', x1: plotLeft, x2: plotRight, y1: trackY, y2: trackY }));
        grid.push(h('line', { class: 'zoom-thumb', x1: thumbX, x2: thumbX + thumbWidth, y1: trackY, y2: trackY }));
      }

      // Единицы шкал — над панелями, от левого края рамки
      if (yScale !== null && this.valueUnit !== '') {
        grid.push(h('text', { class: 'unit', x: plotLeft, y: plotTop - 6 }, this.valueUnit));
      }

      if (volumeScale !== null && this.volumeUnit !== '') {
        grid.push(h('text', { class: 'unit', x: plotLeft, y: volumeTop - 6 }, this.volumeUnit));
      }

      // Панель цены: ступени, отрезки и точки
      if (yScale !== null) {
        for (const tick of yTicks) {
          const y = yScale.toPixel(tick);

          grid.push(h('line', { class: 'grid', x1: plotLeft, x2: plotRight, y1: y, y2: y }));
          grid.push(h('text', { class: 'tick y', x: plotLeft - TICK_GAP_PX, y: y + 4 }, this.formatTick(tick)));
        }

        for (const layer of layers) {
          fillNodes.push(...this.renderStepFills(layer, xScale, yScale));
          stepNodes.push(...this.renderSteps(layer, xScale, yScale));
          rangeNodes.push(...this.renderRanges(layer, xScale, yScale));
        }

        dotNodes.push(...this.renderDots(layers, xScale, yScale));
      }

      // Панель объёма
      if (volumeScale !== null) {
        for (const tick of volumeTicks) {
          const y = volumeScale.toPixel(tick);

          grid.push(h('line', { class: 'grid', x1: plotLeft, x2: plotRight, y1: y, y2: y }));
          grid.push(h('text', { class: 'tick y', x: plotLeft - TICK_GAP_PX, y: y + 4 }, this.formatTick(tick)));
        }

        for (const layer of layers) {
          // Колонка периода сквозная — на всю высоту обеих панелей: любое место графика относится к какому-то месяцу.
          // Точки, отрезки и ступени лежат поверх колонок, над ними работают их собственные тултипы и ссылки
          barNodes.push(...this.renderBars(layer, xScale, volumeScale, yScale !== null ? plotTop : volumeTop));
        }
      }

      return [
        h('g', { class: 'grid' }, grid),
        h('g', { class: 'fills' }, fillNodes),
        h('g', { class: 'bars' }, barNodes),
        h('g', { class: 'steps' }, stepNodes),
        h('g', { class: 'ranges' }, rangeNodes),
        h('g', { class: 'dots' }, dotNodes),
      ];
    },

    /**
     * Ширина колонки подписей делений — по самой длинной подписи, измеренной шрифтом графика.
     * Оценка по числу символов давала лишний отступ слева: «1,5 тыс» и «60» в символах несопоставимы
     */
    ticksWidth(ticks: number[]): number {
      if (measureContext === null) {
        measureContext = document.createElement('canvas').getContext('2d');
      }

      let result = 0;

      if (measureContext !== null) {
        measureContext.font = `${TICK_FONT_SIZE_PX}px ${getComputedStyle(this.$el as Element).fontFamily}`;

        for (const tick of ticks) {
          result = Math.max(result, measureContext.measureText(this.formatTick(tick)).width);
        }
      }

      return Math.ceil(result);
    },

    /** Заливка под ступенями до низа графика — зона «ниже бюджета» (только для слоёв с fill) */
    renderStepFills(layer: ChartLayer, xScale: ChartScale, yScale: ChartScale): VNode[] {
      const result: VNode[] = [];

      if (layer.isFilled === true) {
        for (const step of layer.steps) {
          const startTime = Math.max(step.start.getTime(), xScale.domainStart);

          let endTime: number;

          if (step.end === null) {
            endTime = xScale.domainEnd;
          } else {
            endTime = Math.min(step.end.getTime(), xScale.domainEnd);
          }

          if (endTime > startTime) {
            const x = xScale.toPixel(startTime);
            const y = yScale.toPixel(step.value);

            result.push(h('rect', {
              class:  ['fill', layer.color],
              x:      x,
              y:      y,
              width:  xScale.toPixel(endTime) - x,
              height: yScale.rangeStart - y,
            }));
          }
        }
      }

      return result;
    },

    /**
     * Столбики объёма. Над каждым — невидимая колонка на всю высоту панели объёма:
     * тултип ловится наведением на период, а не прицеливанием в низкий столбик
     */
    renderBars(layer: ChartLayer, xScale: ChartScale, volumeScale: ChartScale, panelTop: number): VNode[] {
      const result: VNode[] = [];

      for (const bar of layer.bars) {
        const startTime = Math.max(bar.start.getTime(), xScale.domainStart);
        const endTime = Math.min(bar.end.getTime(), xScale.domainEnd);

        // Пустой период (value 0) — без столбика, но с колонкой наведения
        if (endTime > startTime) {
          const x = xScale.toPixel(startTime);
          const y = volumeScale.toPixel(bar.value);
          // Зазор в пиксель между соседними столбиками
          const width = Math.max(xScale.toPixel(endTime) - x - 1, 1);

          result.push(h('g', {
            class:        ['bar-column', layer.color, { clickable: bar.action !== null }],
            onClick:      () => {
              if (this.isDragMoved === false) {
                bar.action?.callback();
              }
            },
            // Якорь — вся колонка, тултип встаёт под ней. Тултип периода без ссылок пассивный (см. renderTooltip):
            // ни сам тултип, ни его cloud-маска не перехватывают мышь — соседние колонки и клик по месяцу работают
            onMouseenter: (event: MouseEvent) => this.showTooltip(event, layer, bar.title, null, false, { x, width: width + 1 }),
            onMouseleave: this.scheduleHide,
          }, [
            h('rect', { class: 'hit', x: x, y: panelTop, width: width + 1, height: volumeScale.rangeStart - panelTop }),
            bar.value > 0 ? h('rect', { class: ['bar', layer.color], x: x, y: y, width: width, height: volumeScale.rangeStart - y }) : null,
          ]));
        }
      }

      return result;
    },

    renderSteps(layer: ChartLayer, xScale: ChartScale, yScale: ChartScale): VNode[] {
      const result: VNode[] = [];
      const steps = [...layer.steps].sort((a, b) => a.start.getTime() - b.start.getTime());

      steps.forEach((step, index) => {
        const startTime = Math.max(step.start.getTime(), xScale.domainStart);

        let endTime: number;

        if (step.end === null) {
          endTime = xScale.domainEnd;
        } else {
          endTime = Math.min(step.end.getTime(), xScale.domainEnd);
        }

        if (endTime > startTime) {
          const x1 = xScale.toPixel(startTime);
          const x2 = xScale.toPixel(endTime);
          const y = yScale.toPixel(step.value);

          result.push(h('path', { class: ['step', layer.color, { dashed: layer.isDashed }], d: `M${x1},${y}H${x2}` }));

          // Невидимая толстая линия поверх — в двухпиксельную не попасть мышью
          result.push(h('path', {
            class:        'hit',
            d:            `M${x1},${y}H${x2}`,
            onMouseenter: (event: MouseEvent) => this.showTooltip(event, layer, step.title, null, true),
            onMouseleave: this.scheduleHide,
          }));

          const next = steps[index + 1];

          if (next !== undefined && step.end !== null && next.start.getTime() === step.end.getTime() && step.end.getTime() < xScale.domainEnd) {
            result.push(h('path', { class: ['step', layer.color, { dashed: layer.isDashed }], d: `M${x2},${y}V${yScale.toPixel(next.value)}` }));
          }
        }
      });

      return result;
    },

    /** Отрезок «было → стало»: засечка на начале, точка на конце; без конца — одна засечка */
    renderRanges(layer: ChartLayer, xScale: ChartScale, yScale: ChartScale): VNode[] {
      const result: VNode[] = [];

      for (const range of layer.ranges) {
        const time = range.date.getTime();

        if (time >= xScale.domainStart && time <= xScale.domainEnd) {
          const x = xScale.toPixel(time);
          const yStart = yScale.toPixel(range.start);
          const children: VNode[] = [
            h('path', { class: 'tick', d: `M${x - RANGE_TICK_HALF_WIDTH},${yStart}H${x + RANGE_TICK_HALF_WIDTH}` }),
          ];

          let yEnd: number;

          if (range.end === null) {
            yEnd = yStart;
          } else {
            yEnd = yScale.toPixel(range.end);

            children.push(h('path', { class: 'line', d: `M${x},${yStart}V${yEnd}` }));
            children.push(h('circle', { class: 'end', cx: x, cy: yEnd, r: DOT_RADIUS }));
          }

          // Невидимая полоса поверх — в тонкий отрезок не попасть мышью
          children.push(h('rect', {
            class:  'hit',
            x:      x - RANGE_TICK_HALF_WIDTH,
            y:      Math.min(yStart, yEnd) - DOT_RADIUS,
            width:  RANGE_TICK_HALF_WIDTH * 2,
            height: Math.abs(yEnd - yStart) + DOT_RADIUS * 2,
          }));

          result.push(h('g', {
            class:        ['range', layer.color, { muted: range.isMuted }],
            // Якорь в точке курсора: отрезок бывает высоким, и тултип под его нижним концом уезжал далеко от мыши —
            // по пути к нему курсор задевал другие точки, и тултип сменялся
            onMouseenter: (event: MouseEvent) => this.showTooltip(event, layer, range.title, range.action, true),
            onMouseleave: this.scheduleHide,
          }, children));
        }
      }

      return result;
    },

    /**
     * Точки всех слоёв, разложенные по экранным ячейкам. Точка, легшая в занятую ячейку,
     * превращает её в кластер: так сливаются и сделки одного дня, и соседние дни на длинном периоде,
     * и точки разных слоёв.
     */
    renderDots(layers: ChartLayer[], xScale: ChartScale, yScale: ChartScale): VNode[] {
      const cells = new Map<string, ChartDot>();

      for (const layer of layers) {
        for (const point of layer.points) {
          const time = point.date.getTime();

          if (time >= xScale.domainStart && time <= xScale.domainEnd) {
            const x = xScale.toPixel(time);
            const y = yScale.toPixel(point.value);
            const key = `${Math.round(x / CLUSTER_CELL_PX)}:${Math.round(y / CLUSTER_CELL_PX)}`;
            const dot = new ChartDot(x, y, layer, point);
            const existing = cells.get(key);

            if (existing === undefined) {
              cells.set(key, dot);
            } else if (existing instanceof ChartCluster) {
              existing.append(dot);
            } else {
              cells.set(key, ChartCluster.fromDot(existing).append(dot));
            }
          }
        }
      }

      const result: VNode[] = [];

      for (const item of cells.values()) {
        const events = {
          onMouseenter: (event: MouseEvent) => this.showDotsTooltip(event, item.dots),
          onMouseleave: this.scheduleHide,
        };

        if (item instanceof ChartCluster) {
          let color: ChartColor | 'mixed';
          let isHollow: boolean;

          if (item.isSingleLayer === true) {
            color = item.layer.color;
            isHollow = item.layer.isHollow;
          } else {
            color = 'mixed';
            isHollow = false;
          }

          result.push(h('g', { class: ['cluster', color, { hollow: isHollow }], ...events }, [
            h('circle', { cx: item.x, cy: item.y, r: CLUSTER_RADIUS }),
            h('text', { x: item.x, y: item.y + 3.5 }, String(item.dots.length)),
          ]));
        } else {
          result.push(h('circle', {
            class: ['dot', item.layer.color, { hollow: item.layer.isHollow }],
            cx:    item.x,
            cy:    item.y,
            r:     DOT_RADIUS,
            ...events,
          }));
        }
      }

      return result;
    },
  },

  render() {
    const layers = ChartLayer.fromSlot(this.$slots.default?.() || []);
    const visibleLayers = layers.filter(layer => this.hiddenLabels.includes(layer.label) === false);

    const plotChildren: VNode[] = [];

    let layout: ChartLayout | null = null;

    if (this.width > 0) {
      layout = this.computeLayout(visibleLayers);
    }

    if (layout !== null && this.loading === false) {
      plotChildren.push(h('svg', { width: this.width, height: this.height }, this.renderPlot(visibleLayers, layout)));
    }

    if (this.loading === true) {
      plotChildren.push(h('p', { class: 'overlay' }, 'Загрузка…'));
    } else if (this.hasDataInPeriod(visibleLayers) === false) {
      plotChildren.push(h('p', { class: 'overlay' }, this.emptyText));
    }

    plotChildren.push(...this.renderTooltip());

    const plot = h('div', {
      class:       { plot: true, draggable: this.zoom > 1, dragging: this.dragStartX !== null && this.isDragMoved === true },
      ref:         'plot',
      style:       { height: `${this.height}px` },
      onWheel:     this.onWheel,
      onMousedown: this.onMouseDown,
    }, plotChildren);

    let figureChildren: VNode[];

    if (this.legend === 'top') {
      figureChildren = [this.renderLegend(layers, layout), plot];
    } else {
      figureChildren = [plot, this.renderLegend(layers, layout)];
    }

    if (this.$slots.description) {
      figureChildren.push(h('figcaption', { class: 'description' }, this.$slots.description()));
    }

    return h('figure', { class: { 'v-chart': true, dark: this.isDark } }, figureChildren);
  },
});
</script>
